import { DialogContent, DialogTitle, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Info, X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useElectricityForm } from "../../hooks/useElectricityForm";
import { ElectricityEntry, Option } from "@/types/electricity";

interface ElectricityModalFormProps {
  onClose: () => void;
  onSave: () => void;
  formEntry: ElectricityEntry;
  onFormEntryChange: (entry: ElectricityEntry) => void;
  sourceOptions: Option[];
  editing: boolean;
}

export const ElectricityModalForm = ({
  onClose,
  onSave,
  formEntry,
  onFormEntryChange,
  sourceOptions,
  editing,
}: ElectricityModalFormProps) => {
  const { t } = useLanguage();
  const {
    dropdownRef,
    search,
    showDropdown,
    energySourceError,
    quantityError,
    touched,
    filteredOptions,
    isFormValid,
    handleSourceChange,
    handleSourceSelect,
    handleInputClick,
    handleSourceBlur,
    handleQuantityChange,
    handleQuantityBlur,
    handleSaveClick,
  } = useElectricityForm({
    formEntry,
    sourceOptions,
    editing,
    onFormEntryChange,
    onSave,
  });

  return (
    <DialogContent className="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>
          {editing ? t("common.edit") : t("common.add")} {t("footprints.categories.electricity.title")}
        </DialogTitle>
      </DialogHeader>

      <div className="grid gap-4 py-4">
        {/* Energy Source */}
        <div className="grid gap-2">
          <Label htmlFor="energySource" className="font-medium">
            {t("electricity.energySource") || "Fonte de Energia"} *
          </Label>
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <input
                id="energySource"
                type="text"
                value={search}
                onChange={handleSourceChange}
                onBlur={handleSourceBlur}
                onClick={handleInputClick}
                placeholder={t("electricity.selectSource") || "Selecione a fonte de energia"}
                autoComplete="off"
                spellCheck="false"
                className={`w-full h-11 px-3 py-2 cursor-pointer pr-10 border rounded-md outline-none focus:outline-none ${
                  energySourceError && touched.energySource ? "border-red-500" : "border-gray-200"
                }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                {search && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSourceSelect({ value: "", label: "" }, e);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full mr-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${showDropdown ? "transform rotate-180" : ""}`}
                />
              </div>
            </div>
            {showDropdown && filteredOptions.length > 0 && (
              <div
                className="absolute z-50 w-full mt-1 border bg-white rounded-md shadow-lg overflow-hidden"
                style={{ maxHeight: "320px", overflowY: "auto" }}
              >
                {filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="w-full text-left px-4 py-3.5 hover:bg-teal-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
                    onMouseDown={(e) => handleSourceSelect(option, e)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {energySourceError && touched.energySource && (
            <div className="flex items-center text-xs text-red-500 mt-1">
              <AlertCircle className="h-3 w-3 mr-1" />
              {energySourceError}
            </div>
          )}
        </div>

        {/* Quantity with kWh unit */}
        <div className="grid gap-2">
          <Label htmlFor="quantity" className="font-medium">
            {t("common.quantity")} *
          </Label>

          {!formEntry.energySource ? (
            <div className="space-y-4">
              <Input
                id="quantity"
                type="number"
                step="0.01"
                min="0"
                value={formEntry.quantity === "" ? "" : formEntry.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                onBlur={handleQuantityBlur}
                placeholder="0.00"
                disabled={!formEntry.energySource}
                className={`py-3 rounded-md text-lg ${
                  quantityError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                }`}
              />

              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 flex items-center gap-3 text-gray-600">
                <Info className="h-5 w-5 text-teal-600 flex-shrink-0" />
                <p className="text-sm">{t("electricity.selectEnergySourceInfo") || "Selecione a fonte de energia"}</p>
              </div>
            </div>
          ) : (
            <div className="relative">
              <Input
                id="quantity"
                type="number"
                step="0.01"
                min="0"
                value={formEntry.quantity === "" ? "" : formEntry.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                onBlur={handleQuantityBlur}
                placeholder="0.00"
                disabled={!formEntry.energySource}
                className={`pr-16 py-3 rounded-md text-lg ${
                  quantityError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-500">
                kWh
              </div>
            </div>
          )}

          {quantityError && touched.quantity && (
            <div className="flex items-center text-xs text-red-500 mt-1">
              <AlertCircle className="h-3 w-3 mr-1" />
              {t("validation.positiveNumber")}
            </div>
          )}
        </div>

        {/* Footprint Info Card */}
        {formEntry.energySource && (
          <div className="bg-cyan-50 border border-cyan-100 rounded-md p-4 mt-2">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                {t("electricity.footprintCalculationInfo") ||
                  "A pegada de carbono e hídrica é calculada tendo em conta a quantidade do consumo e os fatores de emissão específicos para cada fonte de energia."}
              </p>
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          type="button"
          onClick={() => handleSaveClick(t)}
          className="bg-teal-600 text-white hover:bg-teal-700"
          disabled={!isFormValid()}
        >
          {t("common.save")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
